import { useEffect } from 'react';
import chroma from 'chroma-js';
import { useTranslation } from 'react-i18next';
import useStore from '../../store';

export const useColor = (ref) => {
  const showPalette = useStore(state => state.showPalette);
  const { t } = useTranslation();
  const { menu, convy, drawing, info, brush, history, components } = ref;
  const { appleHaptic, showTemporaryHint } = menu.methods;
  const { setTheColorFun, handleLastColors } = brush.methods;
  const { getCanvasCoordinates, getReferenceCoordinates } = drawing.methods;
  const { pipette } = brush;
  const { keyboard } = convy;
  const { ColorHint } = components;

  // Универсальная функция получения цвета
  function getColor({ nativeEvent, fromCanvas }) {
    brush.pipette.pointerType = nativeEvent.pointerType || brush.pipette.pointerType;

    // Сценарий 1: Цвет берется с референса (fromCanvas передан)
    if (fromCanvas) {
      brush.pipette.source = 'reference';
      brush.pipette.canvas = fromCanvas; // Сохраняем для лупы
      const context = fromCanvas.getContext('2d');
      const rect = fromCanvas.getBoundingClientRect();

      const clientX = nativeEvent.touches?.[0]?.clientX || nativeEvent.clientX;
      const clientY = nativeEvent.touches?.[0]?.clientY || nativeEvent.clientY;

      let x = clientX - rect.left;
      let y = clientY - rect.top;

      // Сохраняем локальные координаты для лупы
      brush.pipette.canvasCoordinates = { x, y };

      const pixel = context.getImageData(x, y, 1, 1).data;
      const rgbaColor = `rgba(${pixel[0]}, ${pixel[1]}, ${pixel[2]}, ${pixel[3] / 255})`;
      return chroma(rgbaColor).hex();

    } else {
      // Сценарий 2: Цвет берется с основного холста (работает как раньше)
      brush.pipette.source = 'canvas';
      brush.pipette.canvas = convy.main.ref;
      const context = brush.pipette.canvas.getContext('2d');
      let { x, y } = getCanvasCoordinates(nativeEvent);

      // Сохраняем координаты для лупы
      brush.pipette.canvasCoordinates = { x, y };

      x = Math.max(x, 1);
      y = Math.max(y, 1);
      x = Math.min(x, brush.pipette.canvas.width - 1);
      y = Math.min(y, brush.pipette.canvas.height - 1);
      const pixel = context.getImageData(x, y, 1, 1).data;
      const rgbaColor = `rgba(${pixel[0]}, ${pixel[1]}, ${pixel[2]}, ${pixel[3] / 255})`;
      return chroma(rgbaColor).hex();
    }
  }

  const startPickingColorFromCanvas = ({ nativeEvent, fromCanvas }) => { // <--- добавлен fromCanvas
    const { activeTool, color } = useStore.getState();
    if (activeTool !== 'pipette') return;
    brush.lastActionTime = Date.now();
    pipette.pickedColor = getColor({ nativeEvent, fromCanvas }); // <--- fromCanvas передается дальше
    setTheColorFun(pipette.pickedColor);
    const place = {
      x: nativeEvent.touches?.[0]?.clientX || nativeEvent.clientX,
      y: nativeEvent.touches?.[0]?.clientY || nativeEvent.clientY,
    };
    brush.prevColor = color;
    useStore.setState({ isPipettePicking: true, pickingPlace: place });
    appleHaptic?.('soft');
  };

  const searchForColorFromCanvas = ({ nativeEvent, fromCanvas }) => { // <--- добавлен fromCanvas
    const { activeTool, isPipettePicking } = useStore.getState();
    if (!isPipettePicking || activeTool !== 'pipette') return;
    pipette.pickedColor = getColor({ nativeEvent, fromCanvas }); // <--- fromCanvas передается дальше
    setTheColorFun(pipette.pickedColor);
    const place = {
      x: nativeEvent.touches?.[0]?.clientX || nativeEvent.clientX,
      y: nativeEvent.touches?.[0]?.clientY || nativeEvent.clientY,
    };
    useStore.setState({ pickingPlace: place });
  };

  const pickColorFromCanvas = ({ nativeEvent }) => {
    const activeTool = useStore.getState().activeTool;
    if (activeTool !== 'pipette') return;
    if (!keyboard.altIsDown) {
      useStore.setState({ activeTool: brush.previousTool });
      brush.lastActionTime = Date.now();
    }
    useStore.setState({ isPipettePicking: false });
    handleLastColors();
    appleHaptic?.('soft');
    showTemporaryHint(
      <ColorHint text={t('tooltip.saved_color_taken')} hexColor={pipette.pickedColor} />,
      { force: true, duration: 1500 },
      { backgroundColor: 'rgba(0, 0, 0, 0.3)', minWidth: '100px', padding: '8px' }
    );
  };

  const stopPickingColorFromCanvas = (event) => {
    useStore.setState({ isPipettePicking: false });
  };

  useEffect(() => {
    if (showPalette) {
      brush.prevColor = useStore.getState().color;
    } else {
      handleLastColors();
    }
  }, [showPalette]);

  return {
    startPickingColorFromCanvas,
    searchForColorFromCanvas,
    pickColorFromCanvas,
    stopPickingColorFromCanvas,
  };
};
